import {PowerBIEmbed} from 'powerbi-client-react'
import {models, Report} from 'powerbi-client'

import styled from '@emotion/styled'
import {FC, useEffect, useRef, useState} from 'react'
import IPowerBI from './IPowerBI'
import {getUserData} from '../../modules/auth/core/Auth'

interface Props {
  edit: boolean
  data: IPowerBI
  home: boolean
  action: any
  setAction: any
  selectedFilter: any
}

const StyledChart = styled.div<Props>`
  background-color: white;
  width: 100%;
  height: 100%;

  border: 1px solid #ddd;
  padding: 1rem;

  .report-style-class {
    width: 100%;
    height: 100%;
  }

  .content {
    opacity: ${(props) => (props.edit && !props.home ? 0.5 : 1)};
    pointer-events: ${(props) => (props.edit ? 'none' : 'unset')};
    width: 100%;
    height: 100%;
  }

  &:hover {
    .content {
      opacity: 1;
    }
  }
`

const PowerBI: FC<Props> = (props: Props) => {
  const [page, setPage] = useState<any>(null)
  const [counter, setCounter] = useState<number>(0)
  const [filter, setFilter] = useState<any>(null)
  const [initialFilter, setInitialFilter] = useState<any>(null)
  const [isMultiIndustry, setIsMultiIndustry] = useState<boolean>(true)
  const intervalRef = useRef<number | null>(null)
  const mountedRef = useRef(true)

  function onClickChart() {}

  function isPageConnected(targetPage: any) {
    const iframe = targetPage?.report?.iframe
    return Boolean(iframe?.isConnected && iframe.contentWindow)
  }

  async function handleReportLoaded(loadedReport: Report) {
    setInitialFilter(null)
    try {
      const activePage: any = await loadedReport.getActivePage()
      if (!mountedRef.current || !isPageConnected(activePage)) return
      setPage(activePage)
      if (intervalRef.current !== null) window.clearInterval(intervalRef.current)
      intervalRef.current = window.setInterval(() => {
        if (mountedRef.current) setCounter(Math.random())
      }, 2000)
    } catch (_) {}
  }

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!isPageConnected(page)) return
    try {
      const obj = JSON.parse(props.selectedFilter)
      const json = JSON.parse(obj.data)
      const validFilters = getOnlyValidFilters(json)
      if (validFilters && isPageConnected(page)) void page.setFilters(validFilters).catch(() => {})
    } catch (e) {
      if (filter && isPageConnected(page)) void page.setFilters(filter).catch(() => {})
    }
  }, [props.selectedFilter, page, filter, initialFilter])

  function getOnlyValidFilters(filters: any) {
    if (!initialFilter) return
    const finalFilters: any = []
    initialFilter.forEach((f: any) => {
      const filter = filters.find(
        (item: any) =>
          item.target.table === f.target.table &&
          item.target.column === f.target.column &&
          item.filterType === f.filterType
      )
      finalFilters.push(filter ? filter : f)
    })
    return finalFilters
  }

  async function checkFilters(page: any) {
    try {
      if (!isPageConnected(page)) return
      const filters: any = await page.getFilters()
      if (!isPageConnected(page)) return
      if (!initialFilter) {
        setInitialFilter(filters)
      }
      if (!filter) setFilter(filters)
      if (!props.action || JSON.stringify(filters) !== props.action.data) {
        const actionData = {type: 'update-filter', data: JSON.stringify(filters)}
        props.setAction(actionData)
      }
    } catch (e) {}
  }

  useEffect(() => {
    let cancelled = false
    const syncFilters = async () => {
      if (!page || !isMultiIndustry || !isPageConnected(page)) return
      try {
        const filters: any[] = await page.getFilters()
        if (cancelled || !isPageConnected(page)) return
        const newFilters = filters.filter(
          (f) =>
            !(
              f.target &&
              ((f.target.column === 'TamanhoUL' || f.target.column === 'ProgramaUL') && getUserData()?.company_name != 'UNILEVER')
            )
        )
        if (cancelled || !isPageConnected(page)) return
        await page.setFilters(newFilters)
      } catch (_) {}
    }
    void syncFilters()
    return () => {
      cancelled = true
    }
  }, [page, isMultiIndustry])

  useEffect(() => {
    if (isPageConnected(page)) void checkFilters(page)
  }, [counter])

  return (
    <StyledChart {...props}>
      <div className='content' onClick={onClickChart}>
        <PowerBIEmbed
          embedConfig={{
            type: 'report', // Supported types: report, dashboard, tile, visual and qna
            id: props.data.report_id,
            embedUrl: props.data.embedUrl,
            accessToken: props.data.accessToken,
            tokenType: models.TokenType.Embed,
            settings: {
              panes: {
                filters: {
                  visible: true,
                },
                pageNavigation: {
                  visible: false,
                },
              },
              background: models.BackgroundType.Default,
            },
          }}
          eventHandlers={
            new Map([
              [
                'loaded',
                function (event: any) {
                  //console.log(event);
                  void handleReportLoaded(event.detail.report)
                },
              ],
              ['rendered', function () {}],
              ['error', function () {}],
            ])
          }
          cssClassName={'report-style-class'}
        />
      </div>
    </StyledChart>
  )
}

export default PowerBI
